/**
 * ReengagementModal — per-loan discharged re-engagement panel.
 *
 * Logging uses the page's canonical rich logger (ContactTimeline ->
 * loan_contacts), so every channel (phone/email/sms/meeting), outcome and a
 * full notes box are available, and the gate counts those attempts. Once the
 * threshold is met (4 attempts over 21 days, distinct days) the follow-up email
 * unlocks and sends via sendReengagementEmail.
 *
 * Self-contained + type-checked (the parent page is @ts-nocheck).
 */

import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Mail, Lock, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { functions } from '../../lib/firebase';
import ContactTimeline from '../contacts/ContactTimeline';
import type { LoanContactEntry, MutateContactInput } from '../../hooks/useLoanContacts';
import {
  REENGAGEMENT_CONSENT_BASIS_LABELS,
  type DischargedReengagementConfig,
  type ReengagementConsentBasis,
  type ReengagementEligibility,
} from '../../types/dischargedReengagement';

const TEMPLATES: { key: 'voicemail_followup' | 'after_conversation'; label: string }[] = [
  { key: 'voicemail_followup', label: 'Voicemail follow-up' },
  { key: 'after_conversation', label: 'After a conversation' },
];

const CONSENT_OPTIONS: ReengagementConsentBasis[] = [
  'existing_business_relationship',
  'express_consent',
  'manual_override',
];

interface Props {
  companyId: string;
  loan: { loanId: string; customerName: string };
  loanBroker?: string;
  eligibility: ReengagementEligibility | undefined;
  config: DischargedReengagementConfig | null;
  contactEntries: LoanContactEntry[];
  onMutateContact: (input: MutateContactInput) => Promise<{ ok: boolean; entryId: string }>;
  availableBrokers: { id: string; name: string }[];
  currentUser: { uid?: string; displayName?: string; email?: string; role?: string };
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}

export default function ReengagementModal({
  companyId,
  loan,
  loanBroker,
  eligibility,
  config,
  contactEntries,
  onMutateContact,
  availableBrokers,
  currentUser,
  onClose,
  onChanged,
}: Props) {
  const [templateKey, setTemplateKey] =
    useState<'voicemail_followup' | 'after_conversation'>('voicemail_followup');
  const [email, setEmail] = useState(eligibility?.clientEmail ?? '');
  const [consentBasis, setConsentBasis] = useState<ReengagementConsentBasis>(
    config?.defaultConsentBasis ?? 'existing_business_relationship',
  );
  const [sending, setSending] = useState(false);

  const clientId = eligibility?.clientId ?? null;
  const firstName = useMemo(
    () => (loan.customerName || '').trim().split(/\s+/)[0] || 'there',
    [loan.customerName],
  );

  const minAttempts = eligibility?.minAttempts ?? config?.minAttempts ?? 4;
  const minWindowDays = eligibility?.minWindowDays ?? config?.minWindowDays ?? 21;
  const requireDistinctDays = eligibility?.requireDistinctDays ?? config?.requireDistinctDays ?? true;

  const notOwned = eligibility?.canLogActivity === false;
  const canSendEmail = eligibility?.canSendEmail !== false;
  const noClientLink = !clientId && !notOwned; // logging works; send needs a Client Care link

  // Persist a contact via the audited write path, then refetch eligibility
  // (the re-engagement gate reads loan_contacts).
  const handleMutateContact = async (input: MutateContactInput) => {
    const res = await onMutateContact(input);
    await onChanged();
    return res;
  };

  const send = async () => {
    if (!eligibility?.eligible) return;
    if (!email.trim()) {
      toast.error('Enter the client email');
      return;
    }
    setSending(true);
    try {
      const fn = httpsCallable(functions, 'sendReengagementEmail');
      await fn({
        companyId,
        loanId: loan.loanId,
        templateKey,
        clientEmail: email.trim(),
        firstName,
        consentBasis,
      });
      toast.success('Re-engagement email sent');
      await onChanged();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not send email');
    } finally {
      setSending(false);
    }
  };

  const Stat = ({ label, have, need }: { label: string; have: number; need: number }) => (
    <div className="rounded-lg bg-gray-900/60 px-3 py-2 text-center">
      <div className={`text-lg font-bold ${have >= need ? 'text-green-400' : 'text-gray-200'}`}>
        {have}
        <span className="text-xs font-normal text-gray-500"> / {need}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-gray-700 bg-gray-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-700 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-white">{loan.customerName}</h3>
            <p className="font-mono text-[11px] text-gray-500">{loan.loanId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[75vh] space-y-5 overflow-y-auto px-5 py-4">
          {notOwned ? (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              This client is outside your access scope.
            </div>
          ) : (
            <>
              {/* Gate progress (counts all logged outreach: calls, emails, SMS, meetings) */}
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Attempts" have={eligibility?.attemptCount ?? 0} need={minAttempts} />
                <Stat label="Days apart" have={eligibility?.windowSpanDays ?? 0} need={minWindowDays} />
                {requireDistinctDays && (
                  <Stat
                    label="Distinct days"
                    have={eligibility?.distinctDayCount ?? 0}
                    need={minAttempts}
                  />
                )}
              </div>

              {/* Rich multi-channel contact log (shared ContactTimeline) */}
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
                <ContactTimeline
                  companyId={companyId}
                  loanId={loan.loanId}
                  customerName={loan.customerName}
                  loanBroker={loanBroker}
                  sourcePage="Discharges"
                  entries={contactEntries}
                  availableBrokers={availableBrokers}
                  currentUser={currentUser}
                  onMutate={handleMutateContact}
                />
                <p className="mt-2 text-[11px] text-gray-500">
                  Log every outreach (call, email, SMS, meeting). The send unlocks once the
                  thresholds above are met.
                </p>
              </div>

              {/* Send */}
              {eligibility?.eligible && canSendEmail ? (
                <div className="space-y-3 rounded-lg border border-green-600/40 bg-green-600/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-green-300">
                    <Mail className="h-4 w-4" />
                    Send the follow-up email
                  </div>
                  <div className="flex gap-2">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => setTemplateKey(t.key)}
                        className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium ${
                          templateKey === t.key
                            ? 'border-green-500 bg-green-500/10 text-green-200'
                            : 'border-gray-600 bg-gray-900 text-gray-300'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="client@email.com"
                    className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100"
                  />
                  <select
                    value={consentBasis}
                    onChange={(e) => setConsentBasis(e.target.value as ReengagementConsentBasis)}
                    className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100"
                  >
                    {CONSENT_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        Consent: {REENGAGEMENT_CONSENT_BASIS_LABELS[c]}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-500">
                    Sends from {config?.fromAddressMode === 'noreply' ? 'your no-reply address' : "the broker's own address"}{' '}
                    on your verified domain, with an unsubscribe footer.
                  </p>
                  <button
                    onClick={send}
                    disabled={sending}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    Send email
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-900/40 px-4 py-3 text-sm text-gray-400">
                  <Lock className="h-4 w-4 flex-shrink-0" />
                  {noClientLink
                    ? 'Sending needs this customer linked in Client Care; logging still counts.'
                    : !canSendEmail
                      ? 'Linked access permits contact logging, but only the owning broker or an administrator can send.'
                    : eligibility?.alreadySuppressed
                      ? 'This client has unsubscribed; the email is blocked.'
                      : eligibility?.inCooldown
                        ? 'Recently emailed; in cooldown.'
                        : !eligibility?.hasEmailOnFile
                          ? 'Keep logging attempts; add the client email in Client Care (or enter it when sending).'
                          : 'Keep logging attempts to unlock the email.'}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
